import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Create Auth Context
const AuthContext = createContext(null);

// Provider component that wraps your app and makes auth available to any child component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if token is valid and not expired
  const isTokenValid = useCallback((token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      // Check if token is expired
      return decoded.exp * 1000 > Date.now();
    } catch (err) {
      return false;
    }
  }, []);

  // Login
  const login = async (email, password) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await axios.post('/api/login', { email, password });
      const { token, user } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set auth state
      setToken(token);
      setUser(user);
      
      // Set axios default headers for authenticated requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Register
  const register = async (name, email, password) => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await axios.post('/api/register', { name, email, password });
      const { token, user } = response.data;
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set auth state
      setToken(token);
      setUser(user);
      
      // Set axios default headers for authenticated requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');
    
    // Clear auth state
    setToken(null);
    setUser(null);
    
    // Remove Authorization header
    delete axios.defaults.headers.common['Authorization'];
  };

  // Update user
  const updateUser = (userData) => {
    setUser(prevUser => ({ ...prevUser, ...userData }));
  };

  // Effect for loading user data on initial render or token change
  useEffect(() => {
    const loadUserData = async () => {
      if (!token || !isTokenValid(token)) {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        setIsLoading(false);
        return;
      }
      
      try {
        // Set axios default headers for authenticated requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Fetch current user data
        const response = await axios.get('/api/users/me');
        setUser(response.data);
      } catch (err) {
        // If token is invalid or expired
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        console.error('Failed to load user data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [token, isTokenValid]);

  // Context value
  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    register,
    logout,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook for using the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}